import React, { useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { addDays, addWeeks, format, startOfWeek, subWeeks } from "date-fns";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import { toast } from "react-toastify";
import blockStore from "../../Store/BlockStore";
import channelStore from "../../Store/ChannelStore";
import { getErrorMessage } from "../../utils/errorHandler";

const DAY_LABELS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const DAY_NAMES = [
  "понедельник",
  "вторник",
  "среда",
  "четверг",
  "пятница",
  "суббота",
  "воскресенье",
];
const BLOCK_ROTATIONS = ["block_serial", "block_film", "lions"];

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.day_plans)) return value.day_plans;
  if (Array.isArray(value?.items)) return value.items;
  return [];
};

const parseLocalDate = (value) => {
  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);
  return new Date(year, month - 1, day);
};

const formatDateLabel = (value) => format(parseLocalDate(value), "dd.MM.yyyy");

const getMovieTitle = (row) =>
  row.file?.movies?.name_rus ||
  row.file?.movie?.name_rus ||
  row.file?.movies?.title ||
  row.movie?.name_rus ||
  row.title ||
  `Файл ${row.file_id}`;

const formatDuration = (duration) => {
  const totalSeconds = Number(duration);
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return "—";

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return [hours, minutes, seconds]
    .map((part) => String(part).padStart(2, "0"))
    .join(":");
};

const MainWeeklyPeriods = ({ responseListChannels = [] }) => {
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayPlan, setDayPlan] = useState([]);
  const [loadingDay, setLoadingDay] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [rotatingWeek, setRotatingWeek] = useState(false);
  const [buildingTimeline, setBuildingTimeline] = useState(false);
  const [clearingWeek, setClearingWeek] = useState(false);

  const blockChannels = useMemo(
    () =>
      responseListChannels.filter((channel) =>
        BLOCK_ROTATIONS.includes(channel.rotation)
      ),
    [responseListChannels]
  );

  const selectedChannel = useMemo(
    () =>
      blockChannels.find(
        (channel) => Number(channel.id) === Number(selectedChannelId)
      ) || null,
    [blockChannels, selectedChannelId]
  );

  const weekDays = useMemo(() => {
    const monday = startOfWeek(currentWeek, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, index) =>
      format(addDays(monday, index), "yyyy-MM-dd")
    );
  }, [currentWeek]);

  const isWorking =
    loadingDay ||
    savingOrder ||
    rotatingWeek ||
    buildingTimeline ||
    clearingWeek;

  const loadDayPlan = async (channelId, date, { silent = false } = {}) => {
    setLoadingDay(true);
    try {
      const rows = toArray(await blockStore.getDayPlan(channelId, date))
        .slice()
        .sort((a, b) => Number(a.sort) - Number(b.sort));
      setDayPlan(rows);
      return rows;
    } catch (error) {
      setDayPlan([]);
      const noData = [204, 404].includes(error?.response?.status);
      if (!silent && !noData) {
        toast.error(
          getErrorMessage(error, "Не удалось загрузить дневной план")
        );
      }
      return [];
    } finally {
      setLoadingDay(false);
    }
  };

  const handleChannelClick = async (channel) => {
    const monday = weekDays[0];
    setSelectedChannelId(channel.id);
    setSelectedDay(monday);
    setDayPlan([]);
    await loadDayPlan(channel.id, monday, { silent: true });
  };

  const handleDayClick = async (date) => {
    if (!selectedChannelId) return;
    setSelectedDay(date);
    await loadDayPlan(selectedChannelId, date);
  };

  const changeWeek = async (moveForward) => {
    const nextDate = moveForward
      ? addWeeks(currentWeek, 1)
      : subWeeks(currentWeek, 1);
    const monday = format(
      startOfWeek(nextDate, { weekStartsOn: 1 }),
      "yyyy-MM-dd"
    );

    setCurrentWeek(nextDate);
    setSelectedDay(selectedChannelId ? monday : null);
    setDayPlan([]);
    if (selectedChannelId) {
      await loadDayPlan(selectedChannelId, monday, { silent: true });
    }
  };

  const handleMove = async (index, direction) => {
    const targetIndex = index + direction;
    if (
      isWorking ||
      !selectedChannelId ||
      !selectedDay ||
      targetIndex < 0 ||
      targetIndex >= dayPlan.length
    ) {
      return;
    }

    const reordered = dayPlan.slice();
    [reordered[index], reordered[targetIndex]] = [
      reordered[targetIndex],
      reordered[index],
    ];
    const fileIds = reordered.map((row) =>
      Number(row.file_id || row.file?.id)
    );
    if (fileIds.some((fileId) => !fileId)) {
      toast.error("В дневном плане отсутствует ID файла");
      return;
    }

    setSavingOrder(true);
    try {
      const savedRows = toArray(
        await blockStore.replaceDayPlan({
          channel_id: Number(selectedChannelId),
          date: selectedDay,
          file_ids: fileIds,
        })
      );
      if (savedRows.length) {
        setDayPlan(
          savedRows.slice().sort((a, b) => Number(a.sort) - Number(b.sort))
        );
      } else {
        await loadDayPlan(selectedChannelId, selectedDay);
      }
      await channelStore.getChannels();
      toast.success("Порядок дневного плана изменён");
    } catch (error) {
      toast.error(
        getErrorMessage(error, "Не удалось изменить порядок дневного плана")
      );
    } finally {
      setSavingOrder(false);
    }
  };

  const handleRotateWeek = async () => {
    if (!selectedChannelId || rotatingWeek) return;
    const date = selectedDay || weekDays[0];

    setRotatingWeek(true);
    try {
      await blockStore.rotateWeek(Number(selectedChannelId), date);
      await loadDayPlan(selectedChannelId, date);
      await channelStore.getChannels();
      toast.success(
        `Недельный план канала «${selectedChannel?.name || selectedChannelId}» перемешан`
      );
    } catch (error) {
      toast.error(
        getErrorMessage(error, "Не удалось перемешать недельный план")
      );
    } finally {
      setRotatingWeek(false);
    }
  };

  const handleBuildTimeline = async () => {
    if (!selectedChannelId || buildingTimeline) return;
    const date = selectedDay || weekDays[0];

    setBuildingTimeline(true);
    try {
      await blockStore.buildBlockTimeline(Number(selectedChannelId), date);
      await channelStore.getChannels();
      toast.success(
        `Timeline канала «${selectedChannel?.name || selectedChannelId}» сформирован за неделю ${formatDateLabel(weekDays[0])} — ${formatDateLabel(weekDays[6])}`
      );
    } catch (error) {
      toast.error(
        getErrorMessage(error, "Не удалось сформировать недельный timeline")
      );
    } finally {
      setBuildingTimeline(false);
    }
  };

  const handleClearWeek = async () => {
    if (!selectedChannelId || clearingWeek) return;
    const channelName = selectedChannel?.name || selectedChannelId;
    if (
      !window.confirm(
        `Удалить вещание канала «${channelName}» за неделю ${formatDateLabel(weekDays[0])} — ${formatDateLabel(weekDays[6])}?`
      )
    ) {
      return;
    }

    setClearingWeek(true);
    try {
      const results = [];
      for (const date of weekDays) {
        try {
          await blockStore.replaceDayPlan({
            channel_id: Number(selectedChannelId),
            date,
            file_ids: [],
          });
          results.push({ status: "fulfilled" });
        } catch (reason) {
          results.push({ status: "rejected", reason });
        }
      }
      const removedDays = weekDays.filter(
        (_, index) => results[index].status === "fulfilled"
      );
      const failedDays = weekDays.filter(
        (_, index) => results[index].status === "rejected"
      );
      const describeDays = (dates) =>
        dates
          .map((date) => {
            const index = weekDays.indexOf(date);
            return `${DAY_NAMES[index]} ${formatDateLabel(date)}`;
          })
          .join(", ");

      if (selectedDay && removedDays.includes(selectedDay)) {
        setDayPlan([]);
      } else if (selectedDay) {
        await loadDayPlan(selectedChannelId, selectedDay, { silent: true });
      }
      await channelStore.getChannels();

      if (removedDays.length === 7) {
        toast.success(
          `Удалено вещание канала «${channelName}»: ${describeDays(removedDays)}`
        );
      } else if (removedDays.length) {
        toast.warning(
          `Удалено вещание канала «${channelName}»: ${describeDays(removedDays)}. Не удалось очистить: ${describeDays(failedDays)}`
        );
      } else {
        const firstError = results.find((result) => result.status === "rejected");
        toast.error(
          getErrorMessage(
            firstError?.reason,
            `Не удалось удалить вещание канала «${channelName}»`
          )
        );
      }
    } finally {
      setClearingWeek(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          backgroundColor: "#E0F7FA",
          p: 2,
          borderRadius: "8px 8px 0 0",
        }}
      >
        <Typography variant="h5" align="center" color="primary">
          Недельные планы блочных каналов
        </Typography>
      </Box>

      <Box sx={{ display: "flex", minHeight: 650 }}>
        <Box sx={{ width: "25%" }}>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableBody>
                {blockChannels.map((channel) => (
                  <TableRow
                    key={channel.id}
                    hover
                    onClick={() => handleChannelClick(channel)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      {channel.name}
                      {Number(selectedChannelId) === Number(channel.id) && (
                        <CheckIcon sx={{ color: "green" }} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!blockChannels.length && (
                  <TableRow>
                    <TableCell align="center">Нет блочных каналов</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={{ width: "75%", p: 2 }}>
          <Box sx={{ display: "flex", gap: 1, justifyContent: "center", mb: 2 }}>
            {weekDays.map((day, index) => (
              <Button
                key={day}
                variant="contained"
                color={selectedDay === day ? "secondary" : "primary"}
                disabled={!selectedChannelId || isWorking}
                onClick={() => handleDayClick(day)}
                sx={{ flexDirection: "column", minWidth: 80 }}
              >
                <Typography variant="body2">{day}</Typography>
                <Typography variant="caption">{DAY_LABELS[index]}</Typography>
              </Button>
            ))}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 2 }}>
            <Button
              variant="outlined"
              disabled={isWorking}
              onClick={() => changeWeek(false)}
            >
              Предыдущая неделя
            </Button>
            <Button
              variant="outlined"
              disabled={isWorking}
              onClick={() => changeWeek(true)}
            >
              Следующая неделя
            </Button>
          </Box>

          {selectedChannelId && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, textAlign: "center" }}>
              <Typography variant="body2">
                Неделя: {formatDateLabel(weekDays[0])} — {formatDateLabel(weekDays[6])}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 1.5,
                  mt: 1,
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    rotatingWeek ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <ShuffleIcon />
                    )
                  }
                  onClick={handleRotateWeek}
                  disabled={isWorking}
                >
                  Перемешать
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleBuildTimeline}
                  disabled={isWorking}
                >
                  {buildingTimeline ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Сформировать timeline"
                  )}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={handleClearWeek}
                  disabled={isWorking}
                >
                  {clearingWeek ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Удалить вещание недели"
                  )}
                </Button>
              </Box>
            </Paper>
          )}

          {loadingDay ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : dayPlan.length ? (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={60}>№</TableCell>
                    <TableCell>Фильм</TableCell>
                    <TableCell width={100}>File ID</TableCell>
                    <TableCell width={110}>Длительность</TableCell>
                    <TableCell width={120} align="center">Сдвиг</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dayPlan.map((row, index) => (
                    <TableRow key={row.id || `${row.file_id}-${index}`}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{getMovieTitle(row)}</TableCell>
                      <TableCell>{row.file_id || row.file?.id}</TableCell>
                      <TableCell>{formatDuration(row.file?.duration)}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          disabled={isWorking || index === 0}
                          onClick={() => handleMove(index, -1)}
                          title="Сдвинуть вверх"
                        >
                          <KeyboardArrowUpIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          disabled={isWorking || index === dayPlan.length - 1}
                          onClick={() => handleMove(index, 1)}
                          title="Сдвинуть вниз"
                        >
                          <KeyboardArrowDownIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography align="center" color="text.secondary" sx={{ mt: 8 }}>
              {!selectedChannelId
                ? "Выберите блочный канал"
                : `План на ${selectedDay ? formatDateLabel(selectedDay) : "выбранный день"} пуст`}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default observer(MainWeeklyPeriods);
